import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import { listAudits } from "../../api/audit.api";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import PageState from "../../ui/PageState";
import Select from "../../ui/Select";
import StatusBadge from "../../ui/StatusBadge";
import Table from "../../ui/Table";
import { colors, spacing, typography } from "../../ui/tokens";
import {
  pageHeaderActionsStyle,
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  surfaceCardBodyStyle,
  surfaceCardHeaderStyle,
  surfaceCardStyle,
  toolbarStyle,
} from "../../ui/pagePatterns";
import AuditDetailDrawer from "./AuditDetailDrawer";

const stepLabels = {
  scope_draft: "Scope Draft",
  scope_approval: "Scope Approval",
  audit_start: "Audit Start",
  investigation: "Investigation",
  findings: "Recommendations",
  draft_report: "Draft Report",
  final_report: "Final Report",
};

const shellStyle = {
  ...pageShellStyle,
  gap: spacing.xl,
};

const embeddedShellStyle = {
  display: "grid",
  gap: spacing.lg,
};

const surfaceHeaderStyle = {
  ...surfaceCardHeaderStyle,
  alignItems: "center",
};

const summaryBlockStyle = {
  display: "grid",
  gap: spacing.xs / 2,
};

const eyebrowStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const summaryValueStyle = {
  fontWeight: typography.weight.bold,
  fontSize: typography.size.lg,
  color: colors.text,
};

const toolbarCardBodyStyle = {
  ...surfaceCardBodyStyle,
  ...toolbarStyle,
};

const paginationStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.sm,
  flexWrap: "wrap",
  padding: `${spacing.md}px ${spacing.xl + spacing.sm}px`,
  borderTop: `1px solid ${colors.border}`,
  background: colors.surfaceSubtle,
};

const paginationMetaStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const stepCellStyle = {
  display: "grid",
  gap: spacing.xs / 2,
};

const helperCellStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

export default function AuditList({ embedded = false }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ status: "", current_step: "", q: "", limit: 20, offset: 0 });
  const [drawerAuditId, setDrawerAuditId] = useState(null);

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ["audits", filters],
    queryFn: () =>
      listAudits({
        status: filters.status || undefined,
        current_step: filters.current_step || undefined,
        q: filters.q || undefined,
        limit: filters.limit,
        offset: filters.offset,
      }).then((res) => res.data),
    keepPreviousData: true,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const limit = data?.limit || filters.limit;
  const offset = data?.offset ?? filters.offset;
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit || 1));

  const columns = useMemo(
    () => [
      {
        id: "title",
        header: "Title",
        accessor: (row) => (
          <div style={{ display: "grid", gap: spacing.xs / 2 }}>
            <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>{row.title || "-"}</div>
            <div style={helperCellStyle}>Lead: {row.audit_lead_id || "Unassigned"}</div>
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        render: (row) => <StatusBadge value={row.status} fallbackLabel={row.status || "Unknown"} />,
      },
      {
        id: "current_step",
        header: "Current Step",
        accessor: (row) => (
          <div style={stepCellStyle}>
            <div>{stepLabels[row.current_step] || row.current_step || "-"}</div>
            <div style={helperCellStyle}>{row.current_step || "No workflow step"}</div>
          </div>
        ),
      },
      {
        id: "audit_lead_id",
        header: "Lead",
        accessor: (row) => row.audit_lead_id || "Unassigned",
      },
      {
        id: "universe_item_id",
        header: "Auditable Entity",
        accessor: (row) => row.universe_item_id || "-",
      },
    ],
    []
  );

  const onSearch = (event) => {
    event.preventDefault();
    setFilters((prev) => ({ ...prev, offset: 0 }));
  };

  const renderState = () => {
    if (isError) {
      return (
        <PageState
          state="error"
          align="left"
          title="Audit records are unavailable"
          description={`${error?.message || "Glyder could not load the audit register."} Refresh the workspace to try again.`}
          action={
            <Button type="button" variant="secondary" onClick={() => refetch()}>
              Refresh
            </Button>
          }
        />
      );
    }

    if (isLoading) {
      return (
        <PageState
          state="loading"
          align="left"
          title="Loading audits"
          description="Glyder is preparing current audit status, workflow stages, and linked planning context."
        />
      );
    }

    if (!items.length) {
      return (
        <PageState
          state="empty"
          align="left"
          title="No audits scheduled yet"
          description="Audits are created from approved audit plans. Review the planning cycle and approved items to create the first audit."
          actions={
            <>
              <Button variant="secondary" onClick={() => navigate("/controls")}>
                Review controls
              </Button>
              <Button variant="primary" onClick={() => navigate("/audit/planning")}>
                Open Audit Planning
              </Button>
            </>
          }
        />
      );
    }

    return (
      <div style={{ ...surfaceCardStyle, overflow: "hidden" }}>
        <Table columns={columns} data={items} onRowClick={(row) => setDrawerAuditId(row.id)} />
        <div style={paginationStyle}>
          <div style={paginationMetaStyle}>
            Page {currentPage} of {totalPages}
            {isFetching && !isLoading ? " • Updating" : ""}
          </div>
          <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
            <Button
              type="button"
              onClick={() => setFilters((prev) => ({ ...prev, offset: Math.max(0, offset - limit) }))}
              disabled={offset <= 0}
              variant="secondary"
            >
              Previous
            </Button>
            <Button
              type="button"
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  offset: Math.min((totalPages - 1) * limit, offset + limit),
                }))
              }
              disabled={offset + limit >= total}
              variant="secondary"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={embedded ? embeddedShellStyle : shellStyle}>
      {!embedded && <Breadcrumbs items={[{ label: "Audits" }]} />}

      {!embedded && (
        <div style={pageHeaderStyle}>
          <div style={pageHeaderTitleBlockStyle}>
            <h1 style={pageTitleStyle}>Audits</h1>
            <p style={pageSubtitleStyle}>
              Review active audits, monitor workflow progression, and open planning when new audit work needs to be scheduled.
            </p>
          </div>
          <div style={pageHeaderActionsStyle}>
            <Button variant="secondary" onClick={() => refetch()}>
              Refresh
            </Button>
            <Button variant="primary" onClick={() => navigate("/audit/planning")}>
              Open Audit Planning
            </Button>
          </div>
        </div>
      )}

      <div style={surfaceCardStyle}>
        <div style={surfaceHeaderStyle}>
          <div style={summaryBlockStyle}>
            <div style={eyebrowStyle}>Audit Register</div>
            <div style={summaryValueStyle}>{total} audits</div>
          </div>
          {embedded ? (
            <Button variant="secondary" onClick={() => navigate("/audit/planning")}>
              Open Audit Planning
            </Button>
          ) : null}
        </div>

        <form onSubmit={onSearch} style={toolbarCardBodyStyle}>
          <Input
            label="Search audits"
            placeholder="Search audits"
            value={filters.q}
            onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
            style={{ flex: "1 1 280px", minWidth: 220 }}
          />
          <Select
            label="Status"
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            style={{ minWidth: 170 }}
          >
            <option value="">All statuses</option>
            <option value="Planned">Planned</option>
            <option value="Active">Active</option>
            <option value="Complete">Complete</option>
            <option value="Deferred">Deferred</option>
          </Select>
          <Select
            label="Current step"
            value={filters.current_step}
            onChange={(event) => setFilters((prev) => ({ ...prev, current_step: event.target.value }))}
            style={{ minWidth: 190 }}
          >
            <option value="">All steps</option>
            {Object.entries(stepLabels).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
          <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap", alignSelf: "end" }}>
            <Button type="submit" variant="secondary">
              Apply
            </Button>
          </div>
        </form>
      </div>

      {renderState()}

      <AuditDetailDrawer
        auditId={drawerAuditId}
        onClose={() => setDrawerAuditId(null)}
        onOpenFull={(id) => navigate(`/audit/audits/${id}`)}
      />
    </div>
  );
}
